import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Modal, Field } from '../ui/Modal';
import { Icon } from '../ui/Icon';
import { api, ApiError } from '../../lib/api';
import { toast } from '../../stores/toast.store';
import { fmtEur } from '../../lib/utils';
import { CYCLES, CYCLE_LABEL, METHODS, isoDay, leggiImporto, type Cycle, type PayMethod } from '../../features/admin/adminApi';
import { CATEGORIA_SPESA_LABEL, METODO_LABEL } from '../../features/admin/fatture/fattureApi';

/**
 * Spesa fissa (canone, affitto, abbonamento, rata): nome, a chi, importo senza
 * IVA, IVA, ogni quanto, prossimo addebito, come si paga, tipo di spesa. Salva
 * con Ctrl/Cmd+Invio.
 *
 * Con `recurring` è la modifica di una spesa fissa esistente (Spese › Spese
 * fisse › Modifica): stessi campi, PATCH /admin/recurring/:id. Da ModalsRoot
 * arriva senza: è la creazione, come prima.
 */
export interface RecurringDaModificare {
  id: string;
  name: string;
  counterparty: string | null;
  amountNet: number;
  vatRate: number;
  cycle: Cycle;
  nextDueDate: string | null;
  method: PayMethod | null;
  category: string | null;
}

/** I tipi di spesa che si scelgono qui: stipendi, TFR e compenso stanno in Dipendenti. */
const CATEGORIE = ['SAAS', 'UTENZE', 'AFFITTO', 'AUTO', 'CONSULENTI', 'COLLABORATORI', 'FORNITORI', 'ALTRO'];

export function NewRecurringModal({ onClose, recurring }: { onClose: () => void; recurring?: RecurringDaModificare }) {
  const qc = useQueryClient();
  const modifica = !!recurring;
  const [form, setForm] = useState({
    name: recurring?.name ?? '',
    counterparty: recurring?.counterparty ?? '',
    amountNet: recurring ? String(recurring.amountNet).replace('.', ',') : '',
    vatRate: recurring ? String(Number(recurring.vatRate)) : '22',
    cycle: (recurring?.cycle ?? 'MONTHLY') as Cycle,
    nextDueDate: recurring?.nextDueDate ?? isoDay(),
    method: (recurring?.method ?? 'BONIFICO') as PayMethod,
    category: recurring?.category ?? 'ALTRO',
  });
  // "1.300,00" vale 1300 (prima diventava NaN e il tasto restava spento senza spiegazione).
  const net = leggiImporto(form.amountNet);
  const vat = Number(form.vatRate) || 0;
  const gross = Number.isFinite(net) ? net * (1 + vat / 100) : 0;
  // Un'aliquota che non è fra le solite (es. 5%) resta scelta, non si perde salvando.
  const aliquote = ['22', '10', '4', '0'].includes(form.vatRate) ? ['22', '10', '4', '0'] : ['22', '10', '4', '0', form.vatRate];
  const categorie = CATEGORIE.includes(form.category) ? CATEGORIE : [...CATEGORIE, form.category];

  const save = useMutation({
    mutationFn: () => {
      const body = {
        name: form.name.trim(),
        counterparty: form.counterparty.trim() || null,
        amountNet: net,
        vatRate: vat,
        cycle: form.cycle,
        dayOfMonth: Number(form.nextDueDate.slice(8, 10)) || 1,
        nextDueDate: form.nextDueDate,
        method: form.method,
        category: form.category,
      };
      return modifica
        ? api.patch<{ id: string }>(`/admin/recurring/${recurring!.id}`, body)
        : api.post<{ id: string }>('/admin/recurring', { ...body, startDate: form.nextDueDate, active: true });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin'] });
      toast.ok(modifica ? 'Spesa fissa aggiornata' : 'Spesa fissa creata', `${form.name} · € ${fmtEur(gross, true)} ${CYCLE_LABEL[form.cycle].toLowerCase()}`);
      onClose();
    },
    onError: (err) => toast.alert('Spesa fissa non salvata', err instanceof ApiError ? err.message : 'Riprova'),
  });

  const canSubmit = !!form.name.trim() && Number.isFinite(net) && net > 0 && !!form.nextDueDate && !save.isPending;
  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey) && canSubmit) { e.preventDefault(); save.mutate(); }
  };

  return (
    <Modal
      title={modifica ? 'Modifica la spesa fissa' : 'Nuova spesa fissa'}
      kicker="Spese"
      onClose={onClose}
      size="md"
      footer={
        <>
          <button className="btn btn-ghost" onClick={onClose}>Annulla</button>
          <div className="spacer" />
          <button className="btn btn-primary" onClick={() => save.mutate()} disabled={!canSubmit}>
            {modifica ? 'Salva le modifiche' : 'Crea la spesa fissa'} <Icon name="arrowRight" size={12} />
          </button>
        </>
      }
    >
      <div className="form-grid" onKeyDown={onKeyDown}>
        {/* 26/10/2026: "Tipo di spesa" era largo 4 su 12 e si tagliava ("Assicurazior…"): ora mezza riga. */}
        <Field label="Nome della spesa" required span={12}>
          <input className="inp" autoFocus placeholder="Es. Affitto ufficio, Google Workspace" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        </Field>
        <Field label="Tipo di spesa" span={6}>
          <select className="inp" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
            {categorie.map((c) => <option key={c} value={c}>{CATEGORIA_SPESA_LABEL[c] ?? c}</option>)}
          </select>
        </Field>
        <Field label="A chi la paghiamo" span={6} hint="Facoltativo: serve a riconoscere l'addebito in banca">
          <input className="inp" placeholder="Fornitore" value={form.counterparty} onChange={(e) => setForm({ ...form, counterparty: e.target.value })} />
        </Field>
        <Field label="Importo senza IVA" required span={4} hint={form.amountNet.trim() && !(net > 0) ? 'Scrivi solo il numero, per esempio 1.300,00' : undefined}>
          <input className="inp mono" inputMode="decimal" placeholder="0,00" value={form.amountNet} onChange={(e) => setForm({ ...form, amountNet: e.target.value })} />
        </Field>
        <Field label="Aliquota IVA" span={3}>
          <select className="inp" value={form.vatRate} onChange={(e) => setForm({ ...form, vatRate: e.target.value })}>
            {aliquote.map((a) => <option key={a} value={a}>{a}%</option>)}
          </select>
        </Field>
        <Field label="Importo con IVA" span={5}>
          <div className="inp mono" style={{ display: 'flex', alignItems: 'center', background: 'var(--paper-2)' }}>€ {fmtEur(gross, true)}</div>
        </Field>
        <Field label="Ogni quanto si paga" span={6}>
          <select className="inp" value={form.cycle} onChange={(e) => setForm({ ...form, cycle: e.target.value as Cycle })}>
            {CYCLES.map((c) => <option key={c} value={c}>{CYCLE_LABEL[c]}</option>)}
          </select>
        </Field>
        <Field label="Prossimo addebito" required span={6}>
          <input className="inp mono" type="date" value={form.nextDueDate} onChange={(e) => setForm({ ...form, nextDueDate: e.target.value })} />
        </Field>
        <Field label="Come si paga" span={12}>
          <select className="inp" value={form.method} onChange={(e) => setForm({ ...form, method: e.target.value as PayMethod })}>
            {METHODS.map((m) => <option key={m} value={m}>{METODO_LABEL[m]}</option>)}
          </select>
        </Field>
      </div>
    </Modal>
  );
}
